// A5 (Mini App'lar katalogi) — moderatsiya ma'lumot qatlami (modul-lokal).
// `lib/adapters.ts` / `i18n/*` ga TEGMAYDI (C-sessiya HOT) — devApi.ts naqshi.
//
// Backend LANE-A greenfield: mini-app katalog moderatsiyasi endpoint'lari hali
// yo'q (faqat per-bot GET /admin/bots/:id/apps mavjud). Shu sabab fetcherlar
// FAIL-SOFT (bo'sh massiv), mutatsiyalar deploy paydo bo'lganда POST/DELETE
// qiladi (deploy'gacha 404 → xatolik toast'i, ro'yxat buzilmaydi).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

// ── A5.18 — Moderatsiya navbati (yangi ilovalar) ─────────────────────────────
export interface AppQueueRow {
  id: number
  title: string
  short_name: string
  bot_username: string | null
  url: string
  submitted_at: number | null
  status: 'pending' | 'approved' | 'rejected'
}

export function fetchAppQueue(params: FetchParams): Promise<FetchResult<AppQueueRow>> {
  return safeList<AppQueueRow>('/admin/mini-apps/queue', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.bot_username ?? ''}`),
  )
}

/** approve|reject — POST /admin/mini-apps/:id/moderate {decision, reason}. */
export function moderateApp(id: number, decision: 'approve' | 'reject', reason = ''): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/${id}/moderate`, { decision, reason })
}

// ── A5.19 — URL xavfsizlik tekshiruvi ────────────────────────────────────────
export interface UrlScanRow {
  id: number
  app_title: string
  url: string
  verdict: 'safe' | 'suspicious' | 'malicious' | 'unknown'
  categories: string[]
  scanned_at: number | null
}

export function fetchUrlScans(params: FetchParams): Promise<FetchResult<UrlScanRow>> {
  return safeList<UrlScanRow>('/admin/mini-apps/url-scan', 'results', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.app_title} ${r.url}`),
  )
}

/** Qayta tekshirish — POST /admin/mini-apps/:id/rescan. */
export function rescanAppUrl(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/${id}/rescan`, {})
}

// ── A5.21 — Katalogga qo'shish/olib tashlash ─────────────────────────────────
export interface CatalogAppRow {
  id: number
  title: string
  short_name: string
  category: string | null
  in_catalog: boolean
  featured: boolean
  installs: number
}

export function fetchCatalogApps(params: FetchParams): Promise<FetchResult<CatalogAppRow>> {
  return safeList<CatalogAppRow>('/admin/mini-apps/catalog', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.category ?? ''}`),
  )
}

/** in_catalog on/off — POST /admin/mini-apps/:id/catalog {in_catalog}. */
export function setAppCatalog(id: number, inCatalog: boolean): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/${id}/catalog`, { in_catalog: inCatalog })
}

/** featured on/off — POST /admin/mini-apps/:id/feature {featured}. */
export function setAppFeatured(id: number, featured: boolean): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/${id}/feature`, { featured })
}

// ── A5.22 — Kategoriya va teg'lar ────────────────────────────────────────────
export interface AppCategoryRow {
  id: number
  title: string
  slug: string
  tags: string[]
  app_count: number
  position: number
}

export function fetchAppCategories(params: FetchParams): Promise<FetchResult<AppCategoryRow>> {
  return safeList<AppCategoryRow>('/admin/mini-apps/categories', 'categories', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.tags.join(' ')}`),
  )
}

export interface AppCategoryBody {
  title: string
  slug: string
  tags: string[]
  position: number
}

export function createAppCategory(body: AppCategoryBody): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/mini-apps/categories', body)
}

export function updateAppCategory(id: number, body: AppCategoryBody): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>(`/admin/mini-apps/categories/${id}`, body)
}

export function deleteAppCategory(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/mini-apps/categories/${id}`)
}

// ── A5.23 — Reyting va sharhlar moderatsiyasi ────────────────────────────────
export interface AppReviewRow {
  id: number
  app_title: string
  user: string
  rating: number
  text: string
  status: 'visible' | 'hidden' | 'pending'
  created_at: number | null
}

export function fetchAppReviews(params: FetchParams): Promise<FetchResult<AppReviewRow>> {
  return safeList<AppReviewRow>('/admin/mini-apps/reviews', 'reviews', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.app_title} ${r.user} ${r.text}`),
  )
}

/** approve|hide|delete — POST /admin/mini-apps/reviews/:id/moderate {decision}. */
export function moderateReview(id: number, decision: 'approve' | 'hide' | 'delete'): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/reviews/${id}/moderate`, { decision })
}

// ── A5.24/25 — Ilovani to'xtatish / o'chirish ────────────────────────────────
export interface AppRow {
  id: number
  title: string
  short_name: string
  bot_username: string | null
  status: 'active' | 'suspended'
  installs: number
  created_at: number | null
}

export function fetchApps(params: FetchParams): Promise<FetchResult<AppRow>> {
  return safeList<AppRow>('/admin/mini-apps', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.bot_username ?? ''}`),
  )
}

/** A5.24 — to'xtatish/tiklash — POST /admin/mini-apps/:id/suspend {suspended, reason}. */
export function suspendApp(id: number, suspended: boolean, reason = ''): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/mini-apps/${id}/suspend`, { suspended, reason })
}

/** A5.25 — o'chirish — DELETE /admin/mini-apps/:id. */
export function deleteApp(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/mini-apps/${id}`)
}

/** Modul-lokal RU satrlar (i18n HOT → keyinchalik keyга ko'chiriladi). */
export const APP_MOD_STR = {
  // umumiy
  app: 'Приложение',
  bot: 'Бот',
  url: 'URL',
  status: 'Статус',
  created: 'Создано',
  installs: 'Установки',
  done: 'Готово',
  failed: 'Не удалось выполнить',
  cancel: 'Отмена',
  save: 'Сохранить',
  category: 'Категория',
  // A5.18 queue
  queueTitle: 'Модерация новых мини-приложений',
  submitted: 'Отправлено',
  approve: 'Одобрить',
  reject: 'Отклонить',
  approved: 'Одобрено',
  rejected: 'Отклонено',
  pending: 'На модерации',
  rejectTitle: 'Отклонить приложение?',
  rejectDesc: 'Разработчик получит уведомление с причиной. Приложение не попадёт в каталог.',
  moderated: 'Решение сохранено',
  queueEmpty: 'Очередь пуста',
  // A5.19 url scan
  urlScanTitle: 'Проверка безопасности URL',
  verdict: 'Вердикт',
  safe: 'Безопасно',
  suspicious: 'Подозрительно',
  malicious: 'Вредоносно',
  unknown: 'Не проверено',
  categories: 'Категории',
  scannedAt: 'Проверено',
  rescan: 'Перепроверить',
  rescanned: 'Проверка запущена',
  block: 'Заблокировать',
  scanEmpty: 'Нет результатов сканирования',
  // A5.21 catalog
  catalogTitle: 'Каталог мини-приложений',
  inCatalog: 'В каталоге',
  featured: 'Рекомендуемое',
  addToCatalog: 'Добавить в каталог',
  removeFromCatalog: 'Убрать из каталога',
  makeFeatured: 'Сделать рекомендуемым',
  unfeature: 'Убрать из рекомендуемых',
  catalogUpdated: 'Каталог обновлён',
  catalogEmpty: 'Приложений нет',
  // A5.22 categories
  catTitle: 'Категории и теги',
  categoryTitle: 'Название категории',
  slug: 'Slug',
  tags: 'Теги (через запятую)',
  position: 'Позиция',
  appCount: 'Приложений',
  addCategory: 'Добавить категорию',
  editCategory: 'Изменить категорию',
  newCategory: 'Новая категория',
  deleteCategory: 'Удалить категорию',
  deleteCatTitle: 'Удалить категорию?',
  deleteCatDesc: 'Приложения из этой категории станут «без категории».',
  catEmpty: 'Категорий пока нет',
  // A5.23 reviews
  reviewsTitle: 'Модерация оценок и отзывов',
  user: 'Пользователь',
  rating: 'Оценка',
  review: 'Отзыв',
  visible: 'Показан',
  hidden: 'Скрыт',
  hide: 'Скрыть',
  show: 'Показать',
  deleteReview: 'Удалить отзыв',
  reviewModerated: 'Отзыв обновлён',
  reviewsEmpty: 'Отзывов нет',
  // A5.24 suspend
  suspendTitle: 'Приостановка приложений',
  active: 'Активно',
  suspended: 'Приостановлено',
  suspend: 'Приостановить',
  resume: 'Возобновить',
  suspendConfirmTitle: 'Приостановить приложение?',
  suspendConfirmDesc: 'Приложение перестанет открываться у пользователей до возобновления.',
  resumeConfirmTitle: 'Возобновить приложение?',
  resumeConfirmDesc: 'Приложение снова станет доступным пользователям.',
  suspendDone: 'Статус обновлён',
  appsEmpty: 'Приложений нет',
  // A5.25 delete
  deleteAppTitle: 'Удаление приложений',
  deleteApp: 'Удалить приложение',
  deleteAppConfirmTitle: 'Удалить приложение безвозвратно?',
  deleteAppConfirmDesc: 'Мини-приложение и его настройки будут удалены. Действие необратимо.',
  appDeleted: 'Приложение удалено',
} as const
